import { weekFileName } from '../lib/weekFileName';
import { DocLangToggle } from './ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import { WeekRangeFilter } from './ui/week-filter';
import i18n from '../i18n';
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Clock, Download, FileSpreadsheet, FileText, Filter,
  FileSignature, UserCog, Pencil, RotateCcw, Trash2, Activity,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { fmtDateTimeWeek } from '../lib/dateFormat';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from './ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  created:        { icon: FileText,      color: 'bg-blue-100 text-blue-700 border-blue-200',         label: i18n.t('auto.lbl.created') },
  signed:         { icon: FileSignature, color: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: i18n.t('auto.lbl.signed') },
  reassigned:     { icon: UserCog,       color: 'bg-amber-100 text-amber-700 border-amber-200',      label: i18n.t('auto.lbl.reassigned') },
  updated:        { icon: Pencil,        color: 'bg-slate-100 text-slate-700 border-slate-200',      label: i18n.t('auto.lbl.edited') },
  edited:         { icon: Pencil,        color: 'bg-slate-100 text-slate-700 border-slate-200',      label: i18n.t('auto.lbl.edited') },
  reverted:       { icon: RotateCcw,     color: 'bg-orange-100 text-orange-700 border-orange-200',   label: i18n.t('auto.lbl.reverted') },
  status_changed: { icon: Activity,      color: 'bg-purple-100 text-purple-700 border-purple-200',   label: i18n.t('auto.lbl.status') },
  deleted:        { icon: Trash2,        color: 'bg-red-100 text-red-700 border-red-200',            label: i18n.t('auto.lbl.deleted') },
};

function todayISO() { return new Date().toISOString().slice(0, 10); }
function aMonthAgoISO() {
  const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10);
}

/**
 * Project-level audit dashboard. Lets ISO/ASME auditors filter every change
 * touching a project (test reports + checklists) by date, doc kind, event
 * kind and actor, then export the filtered slice as Excel or PDF — which
 * is exactly the "everything that touched project X in Q3" deliverable
 * auditors typically request.
 */
export default function ProjectAuditTab({ projectId, projectName }) {
  const { t } = useTranslation();
  const [start, setStart] = useState(aMonthAgoISO());
  const [end, setEnd] = useState(todayISO());
  const [docKind, setDocKind] = useState('all');
  const [eventKind, setEventKind] = useState('all');
  const [actorId, setActorId] = useState('all');
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load actor list (admin returns full list; non-admin gets a 404 — silent).
  useEffect(() => {
    const tok = localStorage.getItem('token') || localStorage.getItem('auth_token');
    axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => setUsers((r.data || []).filter(u => u.is_approved !== false)))
      .catch(() => {});
  }, []);

  const buildParams = () => {
    const p = new URLSearchParams({ project_id: projectId });
    if (start) p.set('start', start);
    if (end) p.set('end', `${end}T23:59:59`);
    if (docKind !== 'all') p.set('doc_kind', docKind);
    if (eventKind !== 'all') p.set('event_kind', eventKind);
    if (actorId !== 'all') p.set('actor_id', actorId);
    return p;
  };

  const fetchEvents = async () => {
    setLoading(true); setError(null);
    try {
      // Reuse the export endpoint? No — the JSON list comes from the
      // per-doc audit endpoint. For project-wide we expand on the
      // server via project_id on the export endpoints; for the on-screen
      // preview we call the same backend index via a small JSON helper:
      const tok = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const r = await axios.get(`${API}/audit/list?${buildParams()}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setEvents(r.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (projectId) fetchEvents(); /* eslint-disable-next-line */ }, [projectId]);

  const exportFile = async (fmt) => {
    try {
      const tok = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const r = await axios.get(`${API}/audit/export.${fmt}?${buildParams()}`, {
        headers: { Authorization: `Bearer ${tok}` },
        responseType: 'blob',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([r.data]));
      const safeName = (projectName || projectId.slice(0, 8)).replace(/[^\w-]+/g, '_');
      link.download = weekFileName(`audit_${safeName}_${start || ''}_${end || ''}.${fmt}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`Audit log exported (${fmt.toUpperCase()})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    }
  };

  // Group events by document for a clean overview
  const grouped = useMemo(() => {
    const buckets = new Map();
    events.forEach(e => {
      const key = e.doc_id || 'unknown';
      if (!buckets.has(key)) {
        buckets.set(key, { doc_id: key, doc_kind: e.doc_kind, doc_number: e.doc_number, items: [] });
      }
      buckets.get(key).items.push(e);
    });
    return Array.from(buckets.values()).sort((a, b) => {
      const aLast = a.items[a.items.length - 1]?.at || '';
      const bLast = b.items[b.items.length - 1]?.at || '';
      return bLast.localeCompare(aLast);
    });
  }, [events]);

  return (
    <div className="space-y-3" data-testid="project-audit-tab">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" /> {t('auto.misc.auditFilters')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div>
              <label className="text-xs text-gray-600">{t('auto.misc.from')}</label>
              <Input type="date" value={start} onChange={e => setStart(e.target.value)} className="h-8 text-sm" data-testid="audit-from" />
            </div>
            <div>
              <label className="text-xs text-gray-600">{t('auto.misc.to')}</label>
              <Input type="date" value={end} onChange={e => setEnd(e.target.value)} className="h-8 text-sm" data-testid="audit-to" />
            </div>
            <div>
              <label className="text-xs text-gray-600">{t('weekFilter.week')}</label>
              <div><WeekRangeFilter from={start} to={end} onChange={(f, t2) => { setStart(f); setEnd(t2); }} testId="audit-week-filter" /></div>
            </div>
            <div>
              <label className="text-xs text-gray-600">{t('auto.misc.documentType')}</label>
              <Select value={docKind} onValueChange={setDocKind}>
                <SelectTrigger className="h-8 text-sm" data-testid="audit-doc-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('auto.misc.all')}</SelectItem>
                  <SelectItem value="test_report">{t('auto.misc.testReports')}</SelectItem>
                  <SelectItem value="checklist">{t('auto.misc.checklists')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-600">{t('auto.misc.event')}</label>
              <Select value={eventKind} onValueChange={setEventKind}>
                <SelectTrigger className="h-8 text-sm" data-testid="audit-event-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('auto.misc.allEvents')}</SelectItem>
                  <SelectItem value="created">{t('auto.misc.created')}</SelectItem>
                  <SelectItem value="edited">{t('auto.misc.edited')}</SelectItem>
                  <SelectItem value="signed">{t('auto.misc.signed')}</SelectItem>
                  <SelectItem value="reverted">{t('auto.misc.reverted')}</SelectItem>
                  <SelectItem value="reassigned">{t('auto.misc.reassigned')}</SelectItem>
                  <SelectItem value="status_changed">{t('auto.misc.statusChanged')}</SelectItem>
                  <SelectItem value="deleted">{t('auto.misc.deleted')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-600">{t('auto.misc.actor')}</label>
              <Select value={actorId} onValueChange={setActorId}>
                <SelectTrigger className="h-8 text-sm" data-testid="audit-actor"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('auto.misc.allUsers')}</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name || u.username}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-1 justify-end">
              <Button onClick={fetchEvents} variant="outline" size="sm" className="h-8" data-testid="audit-apply">{t('auto.misc.apply')}</Button>
              <DropdownMenu>
                <DocLangToggle testId="doc-lang-projectaudittab-0" />
                <DropdownMenuTrigger asChild>
                  <Button size="sm" className="h-8 gap-1" disabled={!events.length} data-testid="audit-export-menu">
                    <Download className="h-3.5 w-3.5" /> {t('auto.misc.export')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => exportFile('xlsx')}>
                    <FileSpreadsheet className="h-4 w-4 mr-2" /> {t('auto.misc.excelXlsx')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportFile('pdf')}>
                    <FileText className="h-4 w-4 mr-2" /> {t('auto.misc.pdf')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-500" /> {t('auto.misc.events')}
            <span className="text-xs text-gray-400 font-normal">
              ({events.length} event{events.length !== 1 ? 's' : ''} · {grouped.length} document{grouped.length !== 1 ? 's' : ''})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          {loading && <div className="text-xs text-gray-400">{t('auto.misc.loading')}</div>}
          {error && <div className="text-xs text-red-600">{error}</div>}
          {!loading && !error && events.length === 0 && (
            <div className="text-xs text-gray-400 py-4 text-center">{t('auto.misc.noEventsMatchTheCurrentFilter')}</div>
          )}
          {grouped.map(g => (
            <div key={g.doc_id} className="border rounded-lg mb-2 overflow-hidden" data-testid={`audit-doc-${g.doc_id}`}>
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b">
                <span className="font-mono text-xs font-semibold">{g.doc_number || g.doc_id?.slice(0, 8)}</span>
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border bg-white text-gray-600">
                  {g.doc_kind === 'test_report' ? 'Test Report' : g.doc_kind === 'checklist' ? 'Checklist' : g.doc_kind}
                </span>
                <span className="ml-auto text-[11px] text-gray-500">{g.items.length} event{g.items.length !== 1 ? 's' : ''}</span>
              </div>
              <ol className="relative border-l border-gray-200 ml-5 my-3 mr-3 space-y-2.5">
                {g.items.map((e, idx) => {
                  const meta = KIND_META[e.kind] || KIND_META.updated;
                  const Icon = meta.icon;
                  return (
                    <li key={`${e.kind}-${e.at}-${idx}`} className="ml-3">
                      <span className={`absolute -left-[11px] flex items-center justify-center w-5 h-5 rounded-full ring-4 ring-white ${meta.color}`}>
                        <Icon className="h-2.5 w-2.5" />
                      </span>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-800">{e.label}</span>
                        {e.detail && <span className="text-xs text-gray-500">— {e.detail}</span>}
                      </div>
                      {e.changes?.length > 0 && (
                        <ul className="mt-1 space-y-0.5 text-[11px] text-gray-600 font-mono bg-gray-50 border border-gray-100 rounded px-2 py-1">
                          {e.changes.map((c, ci) => <li key={ci}>{c}</li>)}
                        </ul>
                      )}
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        {e.actor_name || 'system'} · {e.at ? fmtDateTimeWeek(e.at) : '—'}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
